import { useCallback } from 'react';
import * as yup from 'yup';
import { useShallow } from 'zustand/react/shallow';

import { useBusinessLayer } from '@mopla/business-logic';
import { useLogger } from '@mopla/logger';

import { useInternalStore } from '../stores/useInternalStore';
import { ITrueLayerReport } from '../types';

const ALLOWED_ACCOUNT_TYPES = ['TRANSACTION', 'CURRENT', 'SAVINGS'];

const trueLayerReportSchema = yup.object().shape({
	account_type: yup
		.string()
		.required()
		.oneOf(ALLOWED_ACCOUNT_TYPES, 'Account type must be one of allowed types'),
	display_name: yup
		.string()
		.required()
		.test(
			'no-savings-keywords',
			'Display name cannot contain savings keywords',
			(value) => !!value && !/tagesgeld|saving/i.test(value)
		),
	verifiable: yup
		.boolean()
		.required()
		.oneOf([true], 'Account must be verifiable'),
	iban: yup.string().required('IBAN is required'),
});

export const useFetchTrueLayerReports = () => {
	const logger = useLogger('payment');
	const { api } = useBusinessLayer();
	const { setIsLoading, setTrueLayerAuthCode, setTrueLayerReportedAccounts } =
		useInternalStore(
			useShallow((s) => ({
				setIsLoading: s.setIsLoading,
				setTrueLayerAuthCode: s.setTrueLayerAuthCode,
				setTrueLayerReportedAccounts: s.setTrueLayerReportedAccounts,
			}))
		);

	const fetchAccessToken = useCallback(
		async ({ authCode, redirectUrl }: IFetchOptions) => {
			setIsLoading(true);

			try {
				const response = await api.get(
					`/api/truelayer/accounts?code=${authCode}&redirectUri=${encodeURIComponent(
						redirectUrl as string
					)}`
				);

				const reports = response.report as ITrueLayerReport[];

				/** Validate and filter accounts using yup schema */
				const accounts: ITrueLayerReport[] = reports.filter((account) =>
					trueLayerReportSchema.isValidSync(account)
				);

				const seenIds = new Set();

				const uniqueById = accounts.filter((account: ITrueLayerReport) => {
					if (seenIds.has(account.iban)) {
						return false;
					}
					seenIds.add(account.iban);

					return true;
				});

				setTrueLayerReportedAccounts(uniqueById);
				setTrueLayerAuthCode(null);

				logger.info(
					'[useFetchTrueLayerReports] Accounts are fetched from TrueLayer',
					{
						allAccountsCount: accounts.length,
						uniqueAccountsCount: uniqueById.length,
					}
				);

				return uniqueById;
			} catch (error) {
				logger.error('Error fetching access token', error);
				return null;
			} finally {
				setIsLoading(false);
			}
		},
		[
			api,
			setIsLoading,
			setTrueLayerAuthCode,
			setTrueLayerReportedAccounts,
			logger,
		]
	);

	return fetchAccessToken;
};

interface IFetchOptions {
	authCode: string;
	redirectUrl: string;
}
